import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Loader2, TrendingDown, TrendingUp, Minus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";

const TrendIcon = ({ trend }) => {
  if (trend === "shorter") return <TrendingDown className="w-4 h-4 text-rose-300" />;
  if (trend === "longer") return <TrendingUp className="w-4 h-4 text-amber-300" />;
  return <Minus className="w-4 h-4 text-emerald-300" />;
};

const CycleInsightsBanner = () => {
  const { token } = useAuth();
  const [insight, setInsight] = useState(null);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return localStorage.getItem(`scf_insight_dismissed_${today}`) === "1";
  });

  useEffect(() => {
    if (dismissed) { setLoading(false); return; }
    const fetchInsight = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/ai/cycle-insights`, {
          headers,
          withCredentials: true,
        });
        if (res.data?.insight) {
          setInsight(res.data.insight);
          setContext(res.data.context);
        }
      } catch {
        // silent — banner just hides
      } finally {
        setLoading(false);
      }
    };
    fetchInsight();
  }, [token, dismissed]);

  const handleDismiss = () => {
    const today = new Date().toISOString().slice(0, 10);
    localStorage.setItem(`scf_insight_dismissed_${today}`, "1");
    setDismissed(true);
  };

  if (loading) return null;
  if (dismissed || !insight) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12 }}
        data-testid="cycle-insights-banner"
      >
        <Card className="glass border-purple-400/30 bg-gradient-to-br from-purple-500/10 via-pink-500/5 to-transparent">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 p-2 rounded-full bg-purple-500/20">
                <Sparkles className="w-4 h-4 text-purple-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-purple-200 text-xs uppercase tracking-wider">Cycle Insight</p>
                  {context?.trend && (
                    <span className="flex items-center gap-1 text-xs text-mystic-200/70">
                      <TrendIcon trend={context.trend} />
                      {context.trend === "steady" ? "steady" : `${Math.abs(context.delta_days || 0)}d ${context.trend}`}
                    </span>
                  )}
                </div>
                <p className="text-mystic-100 text-sm leading-relaxed" data-testid="cycle-insight-text">
                  {insight}
                </p>
              </div>
              <button
                onClick={handleDismiss}
                className="text-mystic-400 hover:text-mystic-200 text-lg leading-none"
                data-testid="dismiss-insight-btn"
                aria-label="Dismiss insight"
              >
                ×
              </button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default CycleInsightsBanner;
